import { Card, Col, Modal, Row, Select, Space, Table, Tag } from 'antd'
import React, { useEffect, useState, useMemo } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import usePage from 'src/hooks/usePage'
import { searchDeleteUserReport } from 'src/api/reports'
import { formatDateForDDMMMYYYY } from 'src/utils/timestamp'

import UserDeleteAccountSearch from './userDeleteAccountSearch'
import { mncmccOptions } from 'src/constants'
import { EyeOutlined, ProfileOutlined } from '@ant-design/icons'
import { Link } from 'react-router-dom'

const { Column } = Table

const UserDeleteAccountPage: React.FC = () => {
  const { pageData, onQuery, appendSearchForm, submitSearchForm } = usePage({
    getData: searchDeleteUserReport,
    searchData: searchDeleteUserReport,
    searchType: 'POST',
  })

  const searchCriteria = useMemo(() => {
    const { buttonAction, page, pageSize, ...criteria } = pageData.query
    return JSON.stringify(criteria)
  }, [pageData.query])

  const [isModalVisible, setIsModalVisible] = useState(false)
  const [selectedUser, setSelectedUser] = useState<any>(null)

  const handleProfileClick = (record) => {
    setSelectedUser(record)
    setIsModalVisible(true)
  }

  const handlePdfClick = () => {
    appendSearchForm({ buttonAction: 'Generate Pdf' })
    submitSearchForm()
  }

  const handleCsvClick = () => {
    appendSearchForm({ buttonAction: 'Generate Csv' })
    submitSearchForm()
  }

  const mncmccMap = Object.fromEntries(
    mncmccOptions.map(({ value, label }) => [value, label])
  )

  return (
    <>
      <UserDeleteAccountSearch
        appendSearchForm={appendSearchForm}
        submitSearchForm={submitSearchForm}
      />

      <Row
        justify="space-between"
        align="middle"
        style={{ marginBottom: 10, marginTop: 5 }}
      >
        <Col></Col>
        <Col>
          <Select
            placeholder="Export"
            style={{ width: 160 }}
            onSelect={(value) => {
              if (value === 'pdf') {
                handlePdfClick()
              } else if (value === 'csv') {
                handleCsvClick()
              }
            }}
            allowClear
          >
            <Select.Option value="pdf">Export as PDF</Select.Option>
            <Select.Option value="csv">Export as CSV</Select.Option>
          </Select>
        </Col>
      </Row>
      <DndProvider backend={HTML5Backend}>
        <Table
          className="table-sorting"
          rowKey="eventId"
          dataSource={pageData.data}
          loading={pageData.loading}
          pagination={{
            current: parseInt(pageData.query.page) + 1,
            total: pageData.total,
            pageSize: parseInt(pageData.query.pageSize) || 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '25', '50', '100'],
            onChange: (p, ps) => {
              appendSearchForm({ buttonAction: '' })
              onQuery({ page: (p - 1).toString(), pageSize: ps.toString() })
            },
          }}
        >
          <Column dataIndex="userName" title="User Name" />
          <Column
            dataIndex="accessLevel"
            title="User Type"
            render={(text) => {
              const colorStyle: React.CSSProperties = {
                color: 'white',
                textAlign: 'center',
                width: 100,
                display: 'inline-block',
                borderRadius: '4px',
                padding: '0 8px',
              }

              switch (text) {
                case 'FREEMIUM':
                  colorStyle.backgroundColor = 'rgba(158, 54, 187, 0.78)'
                  colorStyle.color = 'rgba(222, 222, 222, 1)'
                  break
                case 'GUEST':
                  colorStyle.backgroundColor = 'rgba(54, 187, 156, 0.78)'
                  colorStyle.color = 'rgba(222, 222, 222, 1)'
                  break
                case 'SUBSCRIBED':
                  colorStyle.backgroundColor = 'rgba(187, 54, 132, 0.78)'
                  colorStyle.color = 'rgba(222, 222, 222, 1)'
                  break
                default:
                  colorStyle.backgroundColor = 'rgba(217, 217, 217, 0.78)'
                  colorStyle.color = 'rgba(222, 222, 222, 1)'
              }

              const displayText = text === 'SUBSCRIBED' ? 'PREMIUM' : text
              return <Tag style={colorStyle}>{displayText}</Tag>
            }}
          />
          <Column dataIndex="deviceType" title="Device" />

          <Column
            dataIndex="duration"
            title="Duration"
            render={(text) => <span>{text} days</span>}
          />

          <Column
            dataIndex="deleteDate"
            title="Delete Date"
            render={(text) => {
              return formatDateForDDMMMYYYY(text)
            }}
          />
          <Column
            dataIndex="gender"
            title="Gender"
            filters={[
              {
                text: 'Male',
                value: 'MALE',
              },
              {
                text: 'Female',
                value: 'FEMALE',
              },
            ]}
            filterMode="tree"
            onFilter={(value: string, record: any) => record.gender === value}
          />
          <Column dataIndex="age" title="Age" />
          <Column dataIndex="providerCountry" title="Country" />
          <Column
            title="Source"
            dataIndex="mncmcc"
            render={(value: string) => mncmccMap[value] || value}
          />
          <Column<{ userName: string }>
            title="Action"
            key="action"
            render={(_, record) => (
              <Space size="middle">
                <a
                  onClick={() => handleProfileClick(record)}
                  className="link-btn"
                  style={{ cursor: 'pointer' }}
                >
                  <ProfileOutlined />
                </a>
                <Link
                  to={`/users/detail/${record.userName}`}
                  className="link-btn"
                >
                  <EyeOutlined />
                </Link>
              </Space>
            )}
          />
        </Table>
      </DndProvider>
      <Modal
        title={
          <span style={{ fontSize: '18px', fontWeight: 600 }}>
            Delete Account Summary
          </span>
        }
        visible={isModalVisible}
        footer={null}
        onCancel={() => setIsModalVisible(false)}
        bodyStyle={{
          padding: '20px',
          fontSize: '14px',
          color: '#fff',
          backgroundColor: '#1a1a1a',
        }}
        style={{
          top: 100,
        }}
      >
        {selectedUser && (
          <div style={{ color: '#ddd' }}>
            <p style={{ marginBottom: '8px' }}>
              <strong style={{ color: 'rgb(140 130 130)' }}>Username:</strong>{' '}
              <span style={{ color: '#ccc' }}>{selectedUser.userName}</span>
            </p>
            <p style={{ marginBottom: '12px' }}>
              <strong style={{ color: 'rgb(140 130 130)' }}>
                Reason Type:
              </strong>{' '}
              <span style={{ color: '#ccc' }}>
                {selectedUser.reason || 'N/A'}
              </span>
            </p>
            <hr
              style={{
                border: '0',
                borderTop: '1px solid #333',
                margin: '12px 0',
              }}
            />
            <p style={{ marginBottom: '6px' }}>
              <strong style={{ color: 'rgb(140 130 130)' }}>Summary:</strong>
            </p>
            <p style={{ lineHeight: '1.6', color: '#ccc' }}>
              {selectedUser.other || 'No additional details provided.'}
            </p>
          </div>
        )}
      </Modal>
    </>
  )
}

UserDeleteAccountPage.displayName = 'UserDeleteAccountPage'
export default UserDeleteAccountPage
